import { useEffect, useRef, useState } from "react";
import { Icons } from "../components/Icons.js";
import { ApiError } from "../lib/api.js";
import { enqueuePhoto, enqueueTextNote } from "../core/captureQueue.js";

/**
 * Éditeur de note écrite / photos (note cumulée) en fenêtre modale. Le texte devient une entrée de
 * la note du jour ; chaque photo est une capture photo (OCR côté serveur si l'option est active).
 *
 * Les envois passent par la file hors-ligne (IndexedDB) : ils sont persistés avant tout réseau et
 * rejoués au retour de connexion, comme la capture audio (§7.7). La fenêtre se ferme sans attendre.
 */
export function EditorView({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const t = setTimeout(() => taRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    return () => previews.forEach((u) => URL.revokeObjectURL(u));
  }, [previews]);

  const addPhotos = (capture: boolean) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    if (capture) input.capture = "environment";
    input.onchange = () => {
      const chosen = Array.from(input.files ?? []);
      if (chosen.length === 0) return;
      setFiles((f) => [...f, ...chosen]);
      setPreviews((p) => [...p, ...chosen.map((f) => URL.createObjectURL(f))]);
    };
    input.click();
  };

  const removePhoto = (i: number) => {
    setFiles((f) => f.filter((_, k) => k !== i));
    setPreviews((p) => p.filter((_, k) => k !== i));
  };

  const empty = !text.trim() && files.length === 0;

  const save = async () => {
    if (empty || busy) return;
    setBusy(true);
    setErr(null);
    try {
      // Mise en file (ordre préservé : texte puis photos) ; l'envoi réel part en arrière-plan.
      if (text.trim()) await enqueueTextNote(text.trim());
      for (const f of files) await enqueuePhoto(f, f.name || "photo.jpg");
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Enregistrement impossible.");
      setBusy(false);
    }
  };

  return (
    <div className="editwin-scrim" onClick={onClose}>
      <div className="editwin" onClick={(e) => e.stopPropagation()}>
        <header className="editwin-head">
          <div className="editwin-title">Note écrite / photos</div>
          <button className="iconbtn" onClick={onClose} aria-label="Fermer">
            <Icons.Close size={20} />
          </button>
        </header>
        <div className="editwin-actions">
          <button className="ewbtn primary" onClick={save} disabled={empty || busy}>
            <Icons.Check size={16} /> Enregistrer
          </button>
          <button className="ewbtn" onClick={() => addPhotos(false)} disabled={busy}>
            <Icons.Gallery size={16} /> Galerie
          </button>
          <button className="ewbtn" onClick={() => addPhotos(true)} disabled={busy}>
            <Icons.Camera size={16} /> Photo
          </button>
        </div>
        <div className="editwin-body">
          <textarea ref={taRef} className="editwin-ta" placeholder="Écrivez votre note…" value={text} onChange={(e) => setText(e.target.value)} />
          {previews.length > 0 && (
            <div className="editor-photos">
              {previews.map((p, i) => (
                <div key={i} className="ephoto">
                  <img src={p} alt="" />
                  <button className="ephoto-x" onClick={() => removePhoto(i)} aria-label="Retirer">
                    <Icons.Close size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {err && <div className="editwin-flash err">{err}</div>}
        </div>
      </div>
    </div>
  );
}
